import { ApplicationStatusBadge } from "shared/app/ApplicationStatusBadge";
import { Cell } from "./Cell";
import { FullApplicant } from "../types";
import { Show } from "shared/components/helpers/Show";
import { ContextMenuPopover } from "shared/components/ContextMenuPopover";
import { toast } from "react-toastify";
import { Link, useNavigate } from "react-router-dom";
import { useAuthContext } from "providers/Auth";
import {
  Archive,
  ArrowRight,
  Attachment02,
  Check,
  Clock,
  FlipBackward,
  MessageSquare02,
  User03
} from "untitledui-js-base";
import {
  ApplicantStatus,
  ApplicantType,
  ApplicationStatus,
  ApplicationFeeStatus,
  Applicant,
  PartnerFeatures
} from "shared/types";
import { Dispatch, SetStateAction, useEffect, useState } from "react";
import { getFullName } from "shared/utils/get-full-name";
import { compactNumber } from "shared/utils";
import {
  Button,
  ButtonSize,
  ButtonVariant,
  Hide,
  IconButton,
  IconButtonSize
} from "shared/components";
import { useChatContext } from "stream-chat-react";
import { VerificationProgress } from "./VerificationProgress";
import { hasActionInManualApplication } from "views/applications-id/helpers";

const SuccessRequirementIcon = () => (
  <Check size="16" className="stroke-success-500" />
);

const PendingRequirementIcon = () => (
  <Clock size="16" className="stroke-content-100" />
);

export const ApplicantHeaderCell = ({
  applicant,
  applicationId,
  setCancelApplicantModal,
  setUncancelApplicantModal,
  setChangeRoleModal,
  setRefundModal,
  applicationStatus,
  allowCoSigner,
  allowOccupant,
  setManualApplicationOpen,
  isSubmissionDisclosureFinished
}: {
  applicant:
    | (FullApplicant & {
        is_manual: boolean;
        verifications: Applicant["verifications"];
        preferred_checkout_type: Applicant["preferred_checkout_type"];
        preferred_submission_disclosure_type: Applicant["preferred_submission_disclosure_type"];
      })
    | { email: string };
  applicationId?: string;
  setCancelApplicantModal: Dispatch<SetStateAction<string | undefined>>;
  setUncancelApplicantModal: Dispatch<SetStateAction<string | undefined>>;
  setChangeRoleModal: Dispatch<SetStateAction<FullApplicant | undefined>>;
  setRefundModal: Dispatch<SetStateAction<string | undefined>>;
  applicationStatus?: ApplicationStatus;
  allowCoSigner: boolean;
  allowOccupant: boolean;
  setManualApplicationOpen: (applicantId: number) => void;
  isSubmissionDisclosureFinished: boolean;
}) => {
  const { permissions, type, partnerFeatures } = useAuthContext();
  const navigate = useNavigate();
  // TODO: disable chat for manual applicants
  const hasChat = partnerFeatures.includes(PartnerFeatures.Chat);

  const { client } = useChatContext();

  // eslint-disable-next-line @typescript-eslint/no-unnecessary-condition -- that's expected
  const channel = client?.getChannelById(
    "messaging",
    `chat-id-${"id" in applicant ? applicant.id : applicant.email}`,
    { watch: true }
  );

  const [unreads, setUnreads] = useState(0);

  useEffect(() => {
    const channelWatcher = async () => {
      await channel.watch();

      setUnreads(channel.countUnread());

      channel.on(() => {
        setUnreads(channel.countUnread());
      });
    };

    // eslint-disable-next-line @typescript-eslint/no-unnecessary-condition -- expected
    if (channel) channelWatcher();
  }, [channel]);

  const isApplicant = "first_name" in applicant;
  const isCancelApplicantDisabled =
    [
      ApplicationStatus.Canceled,
      ApplicationStatus.Approved,
      ApplicationStatus.Rejected,
      ApplicationStatus.ConditionalApproved
    ].includes(applicationStatus as ApplicationStatus) ||
    ("application_status" in applicant &&
      applicant.application_status === ApplicantStatus.Canceled);
  const hideCancelApplicant =
    applicationStatus === ApplicationStatus.Canceled ||
    ("application_status" in applicant &&
      applicant.application_status === ApplicantStatus.Canceled);

  return (
    <Cell className="h-[257px] relative" isDataCell>
      <Show
        if={
          "id" in applicant &&
          applicant.is_manual &&
          [ApplicantStatus.Started, ApplicantStatus.Pending].includes(
            applicant.application_status
          )
        }
      >
        <div className="absolute top-0 right-0 pb-0.5 items-center rounded-bl-lg px-3 text-sm font-bold bg-primary-50 text-primary-400 flex gap-2">
          <div className="h-2 w-2 rounded-full bg-primary-500 flex items-center justify-center">
            <div className="h-2 w-2 animate-ping bg-primary-500 rounded-full"></div>
          </div>
          Action required
        </div>
      </Show>
      <div className="w-full">
        <div className="flex justify-between items-center mb-2 max-w-[340px]">
          <div>
            <ApplicationStatusBadge
              status={
                "application_status" in applicant
                  ? applicant.application_status
                  : undefined
              }
              type="applicant"
              refunded={"refunded" in applicant && applicant.refunded}
              isAutoCanceled={
                "canceled_by_auto_cancel" in applicant &&
                applicant.canceled_by_auto_cancel
              }
            />
          </div>
          <Show if={Boolean(permissions.canViewReports)}>
            <div className="ml-auto">
              <ContextMenuPopover
                contentClassName="z-[12]"
                borderLess={false}
                actions={[
                  {
                    title: (
                      <span className="flex gap-2 items-center">
                        <ArrowRight size="20" className="stroke-content-100" />
                        <p>Review</p>
                      </span>
                    ),
                    disabled: !isApplicant,
                    onClick: () => {
                      if (!applicationId)
                        toast.warning(
                          "Applicant received an invite, but didn't apply yet"
                        );
                      else
                        "id" in applicant &&
                          navigate(
                            `/applications/${applicationId}/reports?applicantId=${applicant.id}`
                          );
                    },
                    tooltip: isApplicant
                      ? undefined
                      : "Invited applications don't have any data to review"
                  },
                  ...("id" in applicant
                    ? [
                        {
                          title: (
                            <span className="flex gap-2 items-center">
                              <User03
                                size="20"
                                className="stroke-content-200"
                              />
                              <p>Change role</p>
                            </span>
                          ),
                          onClick: () => {
                            if (applicant.first_name)
                              setChangeRoleModal(applicant);
                          },
                          tooltip: !(
                            type.isAdmin ||
                            type.isOwner ||
                            permissions.canChangeApplicantRole
                          )
                            ? "You don't have permission to change roles. Please contact your administrator"
                            : !applicant.first_name
                              ? "Cannot change role for invited applicants"
                              : !allowCoSigner && !allowOccupant
                                ? "Applicant is the only role available in this template"
                                : ApplicantStatus.Canceled ===
                                    applicant.application_status
                                  ? "You cannot change role for cancelled applicant"
                                  : ApplicantStatus.Submitted !==
                                      applicant.application_status
                                    ? "You can't change role until application is submitted"
                                    : undefined,
                          disabled:
                            !(
                              type.isAdmin ||
                              type.isOwner ||
                              permissions.canChangeApplicantRole
                            ) ||
                            (!allowCoSigner && !allowOccupant) ||
                            !applicant.first_name ||
                            // Applicant is mid application
                            ![
                              ApplicantStatus.Submitted,
                              ApplicantStatus.Canceled
                            ].includes(applicant.application_status)
                        }
                      ]
                    : []),
                  ...(type.isAdmin || type.isOwner
                    ? [
                        {
                          title: (
                            <span className="flex gap-2 items-center">
                              <Attachment02
                                size="20"
                                className="stroke-content-200"
                              />
                              Upload documents
                            </span>
                          ),
                          onClick: () => {
                            if (!applicationId)
                              toast.warning(
                                "Applicant received an invite, but didn't apply yet"
                              );
                            else if ("id" in applicant)
                              navigate(
                                `/applications/${applicationId}/reports?applicantId=${applicant.id}&report=additional_documents`
                              );
                          },
                          disabled: !isApplicant,
                          tooltip:
                            "Applicants need to start their application before documents can be uploaded"
                        }
                      ]
                    : []),
                  ...((type.isAdmin ||
                    type.isOwner ||
                    permissions.canCancelApplications) &&
                  hideCancelApplicant
                    ? [
                        {
                          title: (
                            <span className="flex gap-2 items-center">
                              <Archive
                                size="20"
                                className="stroke-content-200"
                              />
                              Uncancel application
                            </span>
                          ),
                          onClick: () => {
                            setUncancelApplicantModal(
                              "id" in applicant
                                ? String(applicant.id)
                                : String(applicant.email)
                            );
                          }
                        }
                      ]
                    : [
                        {
                          title: (
                            <span className="flex gap-2 items-center">
                              <Archive
                                size="20"
                                className="stroke-content-200"
                              />
                              Cancel application
                            </span>
                          ),
                          onClick: () => {
                            if (
                              ![
                                ApplicationStatus.Canceled,
                                ApplicationStatus.Approved,
                                ApplicationStatus.Rejected,
                                ApplicationStatus.ConditionalApproved
                              ].includes(applicationStatus as ApplicationStatus)
                            )
                              setCancelApplicantModal(
                                "id" in applicant
                                  ? String(applicant.id)
                                  : String(applicant.email)
                              );
                          },
                          tooltip: hideCancelApplicant
                            ? "Can not cancel a canceled application"
                            : "You can't cancel application once the decisions have been made",
                          disabled: isCancelApplicantDisabled
                        }
                      ]),
                  ...(type.isAdmin || type.isOwner
                    ? [
                        {
                          title: (
                            <span className="flex gap-2 items-center">
                              <FlipBackward
                                size="20"
                                className="stroke-content-200"
                              />
                              Refund application
                            </span>
                          ),
                          disabled: !(
                            "application_status" in applicant &&
                            applicant.application_status ===
                              ApplicantStatus.Submitted &&
                            "application_fee_status" in applicant &&
                            applicant.application_fee_status ===
                              ApplicationFeeStatus.Paid
                          ),
                          tooltip:
                            "application_status" in applicant &&
                            applicant.application_status ===
                              ApplicantStatus.Submitted
                              ? "application_fee_status" in applicant &&
                                applicant.application_fee_status ===
                                  ApplicationFeeStatus.Refunded
                                ? "Refund has already been requested for this application"
                                : applicant.application_fee_status ===
                                    ApplicationFeeStatus.Paid
                                  ? ""
                                  : `You can request a refund if the ${
                                      applicant.role === ApplicantType.Applicant
                                        ? "applicant"
                                        : applicant.role ===
                                            ApplicantType.CoSigner
                                          ? "guarantor"
                                          : ""
                                    } has paid the application fee`
                              : "Refund has already been requested for this application",
                          onClick: () =>
                            setRefundModal(
                              "id" in applicant
                                ? String(applicant.id)
                                : undefined
                            )
                        }
                      ]
                    : [])
                ]}
              />
            </div>
          </Show>
        </div>
        <h3 className="text-sm font-bold text-content-1000 mb-2">
          {isApplicant && applicant.first_name && applicant.last_name
            ? getFullName({
                firstName: applicant.first_name,
                lastName: applicant.last_name,
                middleName: applicant.middle_name,
                short: true
              })
            : applicant.email}
        </h3>
        <p className="text-sm text-content-400">
          {"is_manual" in applicant && applicant.is_manual
            ? "Manual application"
            : "Self-serve application"}
        </p>

        <VerificationProgress
          className="my-4"
          status={
            "application_status" in applicant
              ? applicant.application_status
              : ApplicantStatus.Pending
          }
          completedVerifications={
            "finished_verifications" in applicant
              ? applicant.finished_verifications.finished_steps
              : 0
          }
          totalVerifications={
            "finished_verifications" in applicant
              ? applicant.finished_verifications.total_steps
              : 0
          }
        />

        {"is_manual" in applicant &&
        "id" in applicant &&
        applicant.is_manual &&
        ![ApplicantStatus.Canceled, ApplicantStatus.Submitted].includes(
          applicant.application_status
        ) ? (
          <>
            <Hide
              if={
                !hasActionInManualApplication({
                  ...applicant,
                  verifications: (applicant.verifications || []).flat()
                })
              }
            >
              <Button
                size={ButtonSize.SM}
                variant={ButtonVariant.PRIMARY}
                className="mt-2 w-full"
                onClick={(e) => {
                  e.stopPropagation();
                  e.preventDefault();
                  setManualApplicationOpen(Number(applicant.id));
                }}
              >
                {applicant.application_started_at
                  ? "Continue application"
                  : "Start application"}
              </Button>
            </Hide>

            <Show
              if={
                applicant.preferred_submission_disclosure_type ===
                "send_by_email"
              }
            >
              {isSubmissionDisclosureFinished ? (
                <div className="text-content-1000 text-sm mt-2 flex gap-2 items-center">
                  <SuccessRequirementIcon /> Disclosure signed
                </div>
              ) : (
                <div className="text-content-200 text-sm mt-2 flex gap-2 items-center">
                  <PendingRequirementIcon />
                  Disclosure pending
                </div>
              )}
            </Show>

            <Hide
              if={
                applicant.preferred_checkout_type !== "in_person" &&
                [ApplicantStatus.Submitted, ApplicantStatus.Canceled].includes(
                  applicant.application_status
                )
              }
            >
              <div className="text-sm text-content-200 flex items-center gap-2 mt-2">
                <PendingRequirementIcon />
                Checkout pending
              </div>
            </Hide>
          </>
        ) : null}
        <Show if={"id" in applicant}>
          <Hide
            if={
              "id" in applicant &&
              applicant.is_manual &&
              [
                ApplicantStatus.Pending,
                ApplicantStatus.Finished,
                ApplicantStatus.Started
              ].includes(applicant.application_status)
            }
          >
            <div className="flex items-center gap-2 mt-6">
              <Button size={ButtonSize.SM} className="w-full">
                Open application
              </Button>
              <Show
                if={hasChat && "is_manual" in applicant && !applicant.is_manual}
              >
                <Link
                  to={
                    !isApplicant
                      ? ""
                      : `/applications/${applicationId}/reports?applicantId=${"id" in applicant ? applicant.id : applicant.email}&report=chat`
                  }
                  className="relative shrink-0 block"
                >
                  <IconButton
                    Icon={MessageSquare02}
                    size={IconButtonSize.SM}
                    className="block"
                  />
                  {unreads ? (
                    <div className="bg-primary-500 text-white rounded text-xxs px-1 ml-auto min-w-[16px] text-center absolute -top-1.5 -right-1 outline outline-white">
                      {compactNumber(unreads)}
                    </div>
                  ) : null}
                </Link>
              </Show>
            </div>
          </Hide>
        </Show>
      </div>
    </Cell>
  );
};
